/*{ "parent": "component" }*/
/*#
# makeComponent

`makeComponent(tag: string, bluePrint: XinBlueprint<T>): Promise<XinComponentSpec<T>>`
hydrates [blueprints](/blueprint-loader/) into usable [web-components](/component/).

Here are the relevant interfaces:

```
export interface PartsMap<T = Element> {
  [key: string]: T
}

export type XinBlueprint<T = PartsMap> = (
  tag: string,
  module: XinFactory
) => XinComponentSpec<T> | Promise<XinComponentSpec<T>>

export interface XinComponentSpec<T = PartsMap> {
  type: Component<T>
  lightStyleSpec?: XinStyleSheet
  styleSpec?: XinStyleSheet // deprecated, use lightStyleSpec
}
```

Note that a crucial benefit of blueprints is that the **consumer** of the blueprint gets
to choose the `tagName` of the custom-element.
*/

import { Color } from './color'
import { Component } from './component'
import { vars, varDefault } from './css'
import { XinStyleSheet } from './css-types'
import { bind, on } from './bind'
import { elements, svgElements, mathML } from './elements'
import { ElementCreator, PartsMap } from './xin-types'
import { version } from './version'
import { xin, boxed } from './xin'
import { xinProxy, tosi, boxedProxy } from './xin-proxy'

export interface XinFactory {
  Color: typeof Color
  Component: typeof Component
  elements: typeof elements
  svgElements: typeof svgElements
  mathML: typeof mathML
  vars: typeof vars
  varDefault: typeof varDefault
  xin: typeof xin
  boxed: typeof boxed
  xinProxy: typeof xinProxy
  boxedProxy: typeof boxedProxy
  tosi: typeof tosi
  makeComponent: typeof makeComponent
  bind: typeof bind
  on: typeof on
  version: string
}

export interface XinComponentSpec<T = PartsMap> {
  type: Component<T>
  lightStyleSpec?: XinStyleSheet
  /** @deprecated Use lightStyleSpec instead */
  styleSpec?: XinStyleSheet
}

export interface XinPackagedComponent<T = PartsMap> {
  type: Component<T>
  creator: ElementCreator
}

export const madeComponents: { [key: string]: XinPackagedComponent<any> } = {}

export type XinBlueprint<T = PartsMap> = (
  tag: string,
  module: XinFactory
) => XinComponentSpec<T> | Promise<XinComponentSpec<T>>

export async function makeComponent<T = PartsMap>(
  tag: string,
  blueprint: XinBlueprint<T>
): Promise<XinPackagedComponent<T>> {
  const spec = (await blueprint(tag, {
    Color,
    Component,
    elements,
    svgElements,
    mathML,
    varDefault,
    vars,
    xin,
    boxed,
    xinProxy,
    boxedProxy,
    tosi,
    makeComponent,
    bind,
    on,
    version,
  })) as XinComponentSpec<T>
  const { type } = spec
  // Set static properties from blueprint spec before calling elementCreator
  ;(type as any).preferredTagName = tag
  const lightStyle = spec.lightStyleSpec ?? spec.styleSpec
  if (lightStyle) {
    (type as any).lightStyleSpec = lightStyle
  }
  const packagedComponent = {
    type,
    creator: type.elementCreator(),
  }

  madeComponents[tag] = packagedComponent
  return packagedComponent
}
